import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";
import { getUserChatsActions } from "../getUserChats/getUserChats";
import { GetUserChatsDataTypes } from "../../../../Schema/getUserChats.types";
import {
  resetStreamingChatRes,
  setPreventToGetChats,
  setResetAllStreamed,
} from "../../streamedChat/streamedChat";

import { resetFetchedChats } from "../../subService/subServiceChats";
import { queryClient } from "../../../store";

export type ThirdPartyCreateUserServiceTypes = {
  incognito?: number;
  sub_service_id: number;
  openNewSession?: boolean;
  isAgency?: number;
  voiceChat?: boolean;
  mode?: boolean;
};

export type ThirdPartyCreateUserServiceLoadAction = {
  type: string;
  payload: ThirdPartyCreateUserServiceTypes;
};

export type ThirdPartyCreateUserServiceData = {
  data: {
    user_sub_service: {
      id: string | number;
      unique_token: string;
    };
    data: GetUserChatsDataTypes;
    message_count: number;
    message_limit: number;
  };
};

const ThirdPartyCreateUserService = new ReduxFetchState<
  ThirdPartyCreateUserServiceData | null,
  ThirdPartyCreateUserServiceTypes,
  any
>("thirdPartyCreateUserService");

export function* watchThirdPartyCreateUserService(action: ThirdPartyCreateUserServiceLoadAction) {
  try {
    const { incognito, sub_service_id, openNewSession , isAgency, voiceChat , mode} = action.payload;
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const data: ThirdPartyCreateUserServiceData = yield axios({
      url: `api/v1/ai/${isAgency ? "agency" : "third-party"}/user-sub-service/create`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
        lang,
      },
      data: {
        incognito,
        sub_service_id:isAgency ? 107 : voiceChat ? 49 : sub_service_id,
        agency_id:isAgency,
        mode
      },
    });

    yield put(setResetAllStreamed());
    yield put(resetStreamingChatRes());

    if (openNewSession) {
      yield put(setPreventToGetChats(true));
      yield put(resetFetchedChats());
    }

    yield put(ThirdPartyCreateUserService.actions.loadSuccess({ data: data.data }));
    queryClient.removeQueries({ queryKey: ["getUserHistory", lang] });

    yield put(getUserChatsActions.loadSuccess(null));
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? "مشکلی پیش آمده",
      type: "error",
    });
    yield put(ThirdPartyCreateUserService.actions.loadFailure(e.message));
  }
}

export function* thirdPartyCreateUserServiceSagas() {
  yield takeEvery(ThirdPartyCreateUserService.actionTypes.load, watchThirdPartyCreateUserService);
}

export function useThirdPartyCreateUserService() {
  const { loading, data, ...thirdPartyCreateUserServiceState } = useAppSelector(
    (state) => state.thirdPartyCreateUserService,
  );
  const dispatch = useAppDispatch();

  const dispatchThirdPartyCreateUserService = useCallback(
    (data: ThirdPartyCreateUserServiceTypes) => {
      dispatch(ThirdPartyCreateUserService.actions.load(data));
    },
    [dispatch],
  );

  return {
    ...thirdPartyCreateUserServiceState,
    dispatchThirdPartyCreateUserService,
    thirdPartyCreateUserServiceLoading: loading,
    thirdPartyCreateUserServiceData: data,
  };
}

export const {
  actionTypes: thirdPartyCreateUserServiceActionsTypes,
  actions: thirdPartyCreateUserServiceActions,
  reducer: thirdPartyCreateUserServiceReducer,
} = ThirdPartyCreateUserService;
